import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, InfoNote } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, ClipboardCheck } from "lucide-react";

const SCORECARD = [
  "Problem represented accurately", "Workflow realistic", "Required inputs realistically obtainable",
  "Provider workflow practical", "Lab workflow practical", "Evidence extraction valuable",
  "Evidence traceability valuable", "Rules/evaluation structure appropriate", "READY status appropriate",
  "ACTION NEEDED appropriate", "REVIEW REQUIRED appropriate", "Clarification process practical",
  "Human review realistic", "Override controls sufficient", "Audit trail valuable",
  "Reimbursement Defense Packet valuable", "Provider analytics potentially valuable",
  "Would this reduce manual work?", "Could this reduce reimbursement risk?", "Would Keenan use this?",
  "Could another lab understand the value?", "Could another lab plausibly pay?",
];

export default function ValidationMode() {
  const { roleCode, persona, setValidationOpen } = useApp();
  const [items, setItems] = useState([]);
  const [scores, setScores] = useState({});
  const load = () => api.feedback().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const setScore = (i, v) => setScores((s) => ({ ...s, [i]: v }));
  const submitScorecard = async () => {
    await api.createFeedback({
      role_code: roleCode, reviewer: persona?.name, screen: "/validation",
      feature: "Prototype Validation Scorecard", disposition: "KEEP", priority: "P1",
      comment: "Scorecard submission", scorecard: scores,
    });
    load();
  };

  return (
    <div>
      <PageHeader testid="validation-mode-header" title="Validation Mode & Scorecard"
        subtitle="Capture Kevin & Keenan feedback by screen and workflow, categorized KEEP / MODIFY / REMOVE / RESEARCH."
        actions={<>
          <Button variant="outline" size="sm" data-testid="open-validation-panel" onClick={() => setValidationOpen(true)}><ClipboardCheck className="mr-1.5 h-4 w-4" /> Open Feedback Panel</Button>
          <a href={api.exportFeedbackUrl()} data-testid="vm-export"><Button size="sm" className="bg-slate-900 hover:bg-slate-800"><Download className="mr-1.5 h-4 w-4" /> Export CSV</Button></a>
        </>} />
      <InfoNote className="mb-4">The floating feedback panel (top-right “Validation” button) records per-screen feedback anywhere in the app. Below is the structured 1–5 scorecard mirrored from VALIDATION_SCORECARD.md.</InfoNote>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <div className="mb-3 text-sm font-semibold text-slate-800">Prototype Scorecard (1 = poor · 5 = excellent)</div>
          <div className="space-y-1.5">
            {SCORECARD.map((q, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 py-1" data-testid={`score-row-${i}`}>
                <span className="text-xs text-slate-700">{q}</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} data-testid={`score-${i}-${n}`} onClick={() => setScore(i, n)}
                      className={`h-6 w-6 rounded text-[11px] font-semibold ${scores[i] === n ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{n}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Button className="mt-3 w-full bg-teal-600 hover:bg-teal-700" data-testid="submit-scorecard" onClick={submitScorecard}>Submit Scorecard</Button>
        </Card>

        <Card className="p-4">
          <div className="mb-3 text-sm font-semibold text-slate-800">Recorded Feedback ({items.length})</div>
          {items.length === 0 ? <div className="text-sm text-slate-500">No feedback yet.</div> : (
            <Table>
              <TableHeader><TableRow><TableHead>Feature</TableHead><TableHead>Screen</TableHead><TableHead>Disp.</TableHead><TableHead>Pri.</TableHead><TableHead>Reviewer</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="text-xs font-medium">{it.feature || "—"}</TableCell>
                    <TableCell className="font-mono text-[10px]">{it.screen}</TableCell>
                    <TableCell><span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{it.disposition}</span></TableCell>
                    <TableCell className="text-xs">{it.priority}</TableCell>
                    <TableCell className="text-xs">{it.reviewer}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}
